import {useMemo} from "react";

import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

export const useCriteriaOptions = (options) => {
  const {t} = useTranslation();
  const mayoly = useSelector((state) => state.common.settings.isMayoly);

  const criteriaOptions = useMemo(
    () => [
      {
        id: 0,
        value: "visit_opening",
        label: t("page.scenarios.field.criteria.open_visit_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.visit_opening
                ?.company_standard_greetings || 0,
            key: "company_standard_greetings",
            label: t("input.open_visit_1"),
          },
          {
            id: 1,
            value: options?.criteria_weights?.visit_opening?.small_talk || 0,
            key: "small_talk",
            label: t("input.open_visit_2"),
          },
          {
            id: 2,
            value: options?.criteria_weights?.visit_opening?.purpose_said || 0,
            key: "purpose_said",
            label: t("input.open_visit_3"),
          },
          {
            id: 3,
            value:
              options?.criteria_weights?.visit_opening?.got_approve_to_visit ||
              0,
            key: "got_approve_to_visit",
            label: t("input.open_visit_4"),
          },
          {
            id: 4,
            value:
              options?.criteria_weights?.visit_opening
                ?.used_relationship_history || 0,
            key: "used_relationship_history",
            label: t("input.open_visit_5"),
          },
        ],
      },
      {
        id: 1,
        value: "doctor_segmentation",
        label: t("page.scenarios.field.criteria.doctor_segmentation_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.doctor_segmentation
                ?.check_loyalty_to_brand || 0,
            key: "check_loyalty_to_brand",
            label: t("input.doctor_segmentation_1"),
          },
          {
            id: 1,
            value:
              options?.criteria_weights?.doctor_segmentation
                ?.check_patient_statistics || 0,
            key: "check_patient_statistics",
            label: t("input.doctor_segmentation_2"),
          },
        ],
      },
      {
        id: 2,
        value: "needs_identification",
        label: t("page.scenarios.field.criteria.determining_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.needs_identification
                ?.check_therapeutic_preferences || 0,
            key: "check_therapeutic_preferences",
            label: t("input.determining_1"),
          },
          {
            id: 1,
            value:
              options?.criteria_weights?.needs_identification
                ?.active_listening || 0,
            key: "active_listening",
            label: t("input.determining_2"),
          },
          {
            id: 2,
            value:
              options?.criteria_weights?.needs_identification
                ?.check_previous_visit_results || 0,
            key: "check_previous_visit_results",
            label: t("input.determining_3"),
          },
          {
            id: 3,
            value:
              options?.criteria_weights?.needs_identification
                ?.check_reasons_for_drug_choice || 0,
            key: "check_reasons_for_drug_choice",
            label: t("input.determining_4"),
          },
          ...(mayoly
            ? [
                {
                  id: 4,
                  value:
                    options?.criteria_weights?.needs_identification
                      ?.problem_actualization || 0,
                  key: "problem_actualization",
                  label: t("input.determining_5"),
                },
              ]
            : []),
        ],
      },
      {
        id: 3,
        value: "presentation",
        label: t("page.scenarios.field.criteria.presentation_title"),
        rates: [
          {
            id: 0,
            value: options?.criteria_weights?.presentation?.key_messages || 0,
            key: "key_messages",
            label: t("input.presentation_1"),
          },
          {
            id: 1,
            value: options?.criteria_weights?.presentation?.keywords || 0,
            key: "keywords",
            label: t("input.presentation_2"),
          },
          {
            id: 2,
            value:
              options?.criteria_weights?.presentation
                ?.presentation_according_to_criteria || 0,
            key: "presentation_according_to_criteria",
            label: t("input.presentation_3"),
          },
          {
            id: 3,
            value:
              options?.criteria_weights?.presentation?.relevant_argumentation ||
              0,
            key: "relevant_argumentation",
            label: t("input.presentation_4"),
          },
          {
            id: 4,
            value:
              options?.criteria_weights?.presentation?.control_questions || 0,
            key: "control_questions",
            label: t("input.presentation_5"),
          },
          {
            id: 5,
            value: options?.criteria_weights?.presentation?.FAB_technique || 0,
            key: "FAB_technique",
            label: t(mayoly ? "input.presentation_mayoly_6" : "input.presentation_6"),
          },
          ...(mayoly
            ? [
                {
                  id: 6,
                  value:
                    options?.criteria_weights?.presentation
                      ?.recommendation_algorithm || 0,
                  key: "recommendation_algorithm",
                  label: t("input.presentation_mayoly_7"),
                },
              ]
            : []),
        ],
      },
      {
        id: 4,
        value: "objection_handling",
        label: t("page.scenarios.field.criteria.objections_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.objection_handling
                ?.clarify_objection || 0,
            key: "clarify_objection",
            label: t("input.objections_1"),
          },
          {
            id: 1,
            value:
              options?.criteria_weights?.objection_handling
                ?.four_stages_objection_handling || 0,
            key: "four_stages_objection_handling",
            label: t(mayoly ? "input.objections_mayoly_2" : "input.objections_2"),
          },
          {
            id: 2,
            value:
              options?.criteria_weights?.objection_handling
                ?.competitor_differentiation || 0,
            key: "competitor_differentiation",
            label: t("input.objections_3"),
          },
          {
            id: 3,
            value:
              options?.criteria_weights?.objection_handling?.check_agreement ||
              0,
            key: "check_agreement",
            label: t("input.objections_4"),
          },
        ],
      },
      {
        id: 5,
        value: "visit_closing",
        label: t("page.scenarios.field.criteria.ending_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.visit_closing
                ?.quantitative_agreements || 0,
            key: "quantitative_agreements",
            label: t("input.ending_1"),
          },
          {
            id: 1,
            value: options?.criteria_weights?.visit_closing?.visit_summary || 0,
            key: "visit_summary",
            label: t("input.ending_2"),
          },
          {
            id: 2,
            value:
              options?.criteria_weights?.visit_closing
                ?.patient_portrait_clarification || 0,
            key: "patient_portrait_clarification",
            label: t("input.ending_3"),
          },
          {
            id: 3,
            value:
              options?.criteria_weights?.visit_closing
                ?.closing_control_questions || 0,
            key: "closing_control_questions",
            label: t("input.ending_4"),
          },
          {
            id: 4,
            value:
              options?.criteria_weights?.visit_closing?.doctor_pharmacy_link ||
              0,
            key: "doctor_pharmacy_link",
            label: t("input.ending_5"),
          },
        ],
      },
      {
        id: 6,
        value: "visit_structure",
        label: t("page.scenarios.field.criteria.structure_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.visit_structure?.structure_adherence ||
              0,
            key: "structure_adherence",
            label: t("input.structure_1"),
          },
        ],
      },
      {
        id: 7,
        value: "dialogue_balance",
        label: t("page.scenarios.field.criteria.balance_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.dialogue_balance
                ?.talk_time_distribution || 0,
            key: "talk_time_distribution",
            label: t("input.balance_1"),
          },
          {
            id: 1,
            value:
              options?.criteria_weights?.dialogue_balance?.doctor_engagement ||
              0,
            key: "doctor_engagement",
            label: t("input.balance_2"),
          },
          {
            id: 2,
            value:
              options?.criteria_weights?.dialogue_balance?.dominance_check || 0,
            key: "dominance_check",
            label: t("input.balance_3"),
          },
          ...(mayoly
            ? [
                {
                  id: 3,
                  value:
                    options?.criteria_weights?.dialogue_balance
                      ?.drug_time_distribution || 0,
                  key: "drug_time_distribution",
                  label: t("input.balance_4"),
                },
              ]
            : []),
        ],
      },
      {
        id: 8,
        value: "engagement",
        label: t("page.scenarios.field.criteria.engagement_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.engagement?.emotional_engagement || 0,
            key: "emotional_engagement",
            label: t("input.engagement_1"),
          },
          {
            id: 1,
            value:
              options?.criteria_weights?.engagement?.questions_quality || 0,
            key: "questions_quality",
            label: t("input.engagement_2"),
          },
        ],
      },
      {
        id: 9,
        value: "factual_base",
        label: t("page.scenarios.field.criteria.basis_title"),
        rates: [
          {
            id: 0,
            value:
              options?.criteria_weights?.factual_base?.evidence_quality || 0,
            key: "evidence_quality",
            label: t("input.basis_1"),
          },
          {
            id: 1,
            value: options?.criteria_weights?.factual_base?.research_usage || 0,
            key: "research_usage",
            label: t("input.basis_2"),
          },
          {
            id: 2,
            value:
              options?.criteria_weights?.factual_base?.kols_references || 0,
            key: "kols_references",
            label: t("input.basis_3"),
          },
        ],
      },
    ],
    [options, t, mayoly],
  );

  return criteriaOptions;
};

export const usePhaseOptions = () => {
  const {t} = useTranslation();

  const phaseOptions = useMemo(
    () => [
      {id: 0, value: "primary", label: t("input.primary")},
      {id: 1, value: "secondary", label: t("input.repeated")},
    ],
    [t],
  );

  return phaseOptions;
};
